import React, { useState, useRef, useEffect } from 'react';
import { PriceBlock, Typography, TextField, Modal, Button } from '@hpi-sox/hpstellar-core';
import { Cross } from '@hpi-sox/hpstellar-icons';

import withPageComponents from '../../../page/components/with-page-components';
import AdaptiveImage from '../../../shared/components/adaptive-image';
import { EtrAPI } from '../../../core/src/etr-api-client';
import Recaptcha from '../../../shared/components/recaptcha';
import { useMSRPProps } from '../../../hooks/useProductPrice';

import './share-config.less';
const initialInput = {
    email: '',
    emailError: null,
    sender: '',
    message: 'I customized this PC on HP.com and wanted to share it with you. View the product details below.',
    token: null,
};

const SpecList = ({ title, specs }) => {
    if (specs.length < 1) {
        return null;
    }
    return (
        <div className={'spec-group'}>
            <Typography className={'spec-title'} tag="h4" variant="boldL">
                {title}
            </Typography>
            {specs.map(({ title, value }, idx) => (
                <div className="spec-row" key={`${title}-${idx}`}>
                    <Typography tag="p" variant="boldS" className="spec-name">
                        {title}
                    </Typography>
                    <Typography tag="p" variant="bodyS" className="spec-value">
                        {value}
                    </Typography>
                </div>
            ))}
        </div>
    );
};

const ProductDetail = ({ image, productName, price, sku, configSpecs }) => {
    const msrp = useMSRPProps();
    return (
        <>
            <div className={'product-detail'}>
                {image && <AdaptiveImage src={image} maxWidth={175} impolicy={null} />}
                <div>
                    <Typography tag="h1" variant="titleM">
                        {productName}
                    </Typography>
                    <Typography tag="h1" variant="bodyS">
                        <i>{`Product #: ${sku}`}</i>
                    </Typography>
                    {price && <PriceBlock {...price} style="left" size="medium" msrp={msrp} />}
                </div>
            </div>
            {Object.keys(configSpecs).map(key => {
                const { title, specs } = configSpecs[key];
                return <SpecList key={key} title={title} specs={specs} />;
            })}
        </>
    );
};

function isValidEmail(email) {
    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

const validateEmail = (setEmailError, setEmailAddress) => e => {
    const email = e.target.value;
    const error = email.split(';').reduce((r, e) => r && isValidEmail(e), true)
        ? false
        : 'Invalid email address. Please make sure to use semicolons to separate multiple email addresses';
    setEmailAddress(e.target.value);
    setEmailError(error);
};

const ShareConfig = ({
    baseConfig,
    ctoPrice: price,
    animationType = 'fade-in',
    title = 'Share via email',
    className,
    device,
    onModalClose,
    ...modalProps
}) => {
    const [emailAddress, setEmailAddress] = useState(initialInput.email);
    const [emailError, setEmailError] = useState(initialInput.emailError);
    const [sender, setSender] = useState(initialInput.sender);
    const [token, setToken] = useState();
    const [message, setMessage] = useState(initialInput.message);
    const formRef = useRef();
    useEffect(() => {
        return () => {
            //reset to initial values
            setEmailAddress(initialInput.email);
            setEmailError(initialInput.emailError);
            setSender(initialInput.sender);
            setMessage(initialInput.message);
            setToken(initialInput.token);
        };
    }, [modalProps.isOpen]);
    //track state in mutable form Ref so that recaptch callback can access them.
    formRef.current = {
        sender,
        emailAddress,
        message,
    };
    if (!baseConfig) {
        return null;
    }
    const { image, specCategories = [], title: productName, sku, pdpURL } = baseConfig;
    const isMobile = device === 'mobile';
    const configSpecs = specCategories.reduce(
        (r, cat) => {
            const { optionitems, categorization, title } = cat;
            const specBucket = categorization === 'Primary Specs' ? r.primary : r.additional;
            optionitems.reduce((s, o) => {
                if (o.selected === 'true') {
                    const spec = {
                        title,
                        value: o.name,
                    };
                    specBucket.specs.push(spec);
                }
                return s;
            }, []);
            return r;
        },
        { primary: { title: 'PRIMARY SPECS', specs: [] }, additional: { title: 'ADDITIONAL SPECS', specs: [] } },
    );
    const sendCTOEmail = (captchaToken, onModalClose = () => {}) => {
        EtrAPI.cart
            .emailCTO(
                baseConfig,
                configSpecs.primary.specs,
                configSpecs.additional.specs,
                formRef.current.emailAddress,
                formRef.current.sender,
                formRef.current.message,
                price.salePrice,
                price.regularPrice,
                message !== initialInput.message,
                pdpURL, //TODO: figure out how to get PDP url
                //TODO: need to ask about this authToken value
                undefined,
                captchaToken,
            )
            .then(() => {
                try {
                    onModalClose();
                } catch (e) {}
            })
            .catch(() => {
                try {
                    onModalClose();
                } catch (e) {}
            });
    };

    const isSendButtonDisabled = !emailAddress || emailError;
    const { sendConfigurationGtmAttributes } = baseConfig || {};

    return (
        <>
            <Modal
                animationType={animationType}
                withBackdrop
                {...modalProps}
                modalClassName={`share-config-modal${isMobile ? ` mobile` : ''} `}
            >
                <div className="share-config-content">
                    <button type="button" onClick={onModalClose} className="closeButton" aria-label="Close">
                        <Cross size="m" />
                    </button>
                    <Typography id="modal_title" tag="h2" variant="boldL" isCaps>
                        {title}
                    </Typography>
                    <Typography variant="bodyXS" className="required-label">
                        <b>*</b> REQUIRED
                    </Typography>
                    <TextField
                        footNotePosition="left"
                        fieldProps={{
                            name: 'input',
                            type: 'text',
                            onBlur: validateEmail(setEmailError, setEmailAddress),
                            defaultValue: emailAddress,
                        }}
                        label="Enter email address(es)"
                        required
                        validate
                        error={emailError}
                        footNote="Use a semicolon to separate multiple email addresses"
                    />
                    <div className="form-spacer"></div>
                    <TextField
                        footNotePosition="left"
                        fieldProps={{
                            name: 'input',
                            type: 'text',
                            onChange: e => setSender(e.target.value),
                            defaultValue: sender,
                        }}
                        label="Enter name"
                        footNote="Let your recipient(s) know who is sending this configuration"
                    />
                    <div className="form-spacer"></div>
                    <TextField
                        label=""
                        footNotePosition="left"
                        className="share-config-textarea"
                        fieldProps={{
                            name: 'emailMessage',
                            type: 'text',
                            onChange: e => setMessage(e.target.value),
                            defaultValue: message,
                        }}
                        multiline
                    />
                    {!isMobile && (
                        <ProductDetail
                            image={image}
                            productName={productName}
                            price={price}
                            configSpecs={configSpecs}
                            sku={sku}
                        />
                    )}
                    <Button
                        className="send-config"
                        size="large"
                        variation={isMobile ? 'secondary' : 'primary'}
                        disabled={isSendButtonDisabled}
                        isFullWidth={isMobile}
                        {...(!isSendButtonDisabled && sendConfigurationGtmAttributes
                            ? sendConfigurationGtmAttributes
                            : {})}
                        timeoutSuccess={true}
                        onClick={() => {
                            if (token) {
                                sendCTOEmail(token, onModalClose);
                            } else {
                                window.grecaptcha && window.grecaptcha.execute();
                            }
                        }}
                    >
                        Send Configuration
                    </Button>
                </div>
            </Modal>
            {modalProps.isOpen ? (
                <Recaptcha
                    name="sendCTOEmail"
                    callback={respToken => {
                        setToken(respToken);
                        sendCTOEmail(respToken, onModalClose);
                    }}
                />
            ) : null}
        </>
    );
};

export default withPageComponents(ShareConfig, { components: ['baseConfig', 'ctoPrice'], device: true });
